// frontend/modules/customer-category/components/onboarding/AdditionalInfoStep.tsx
import React, { useEffect } from 'react';
import {
    Form,
    Input,
    InputNumber,
    Select,
    Typography,
    Row,
    Col
} from 'antd';
import { useTheme } from '@/components/common/ThemeProvider';
import {
    CustomerOnboardingData,
    CustomerCategoryFieldDTO
} from '../../types';
import { useFieldConfiguration } from '../../hooks/useFieldConfiguration';
import DynamicFormField from '../DynamicFormField';
import {CustomerCategoryDTO} from "@/shared/api/model";

const { Text } = Typography;
const { Option } = Select;

interface AdditionalInfoStepProps {
    category: CustomerCategoryDTO | null;
    formData: CustomerOnboardingData;
    onUpdateFormData: (data: Partial<CustomerOnboardingData>) => void;
}

const AdditionalInfoStep: React.FC<AdditionalInfoStepProps> = ({
                                                                   category,
                                                                   formData,
                                                                   onUpdateFormData
                                                               }) => {
    const { dark } = useTheme();
    const [form] = Form.useForm();

    const {
        entityFields,
        isLoading,
        fetchEntityFields
    } = useFieldConfiguration();

    // Get custInfo fields from category
    const custInfoFields = (category?.fields || []).filter(
        field => field.entityType === 'CustInfo' && field.isVisible
    );

    // Load entity fields if in freestyle mode
    useEffect(() => {
        if (formData.isFreestyle && !entityFields) {
            fetchEntityFields();
        }
    }, [formData.isFreestyle, entityFields, fetchEntityFields]);

    // Set initial form values from formData
    useEffect(() => {
        form.setFieldsValue(formData.custInfoData);
    }, [form, formData.custInfoData]);

    // When form values change, update parent data
    const handleValuesChange = (changedValues: any, allValues: any) => {
        onUpdateFormData({
            custInfoData: allValues
        });
    };

    const getFieldConfig = (fieldName: string): CustomerCategoryFieldDTO | undefined => {
        return custInfoFields.find(field => field.fieldName === fieldName);
    };

    const renderField = (fieldName: string, metadata: any) => {
        const fieldConfig = getFieldConfig(fieldName);
        
        return (
            <DynamicFormField
                fieldName={fieldName}
                metadata={metadata}
                fieldConfig={fieldConfig}
                category={category}
                entityType="CustInfo"
            />
        );
    };

    // Render fields based on category or freestyle mode
    const renderFields = () => {
        if (formData.isFreestyle && entityFields) {
            // Render all fields in freestyle mode
            return Object.entries(entityFields.custInfoFields).map(([fieldName, metadata]) => (
                <Col xs={24} sm={12} md={8} key={fieldName}>
                    {renderField(fieldName, metadata)}
                </Col>
            ));
        } else if (category) {
            // Render fields based on category configuration
            return custInfoFields
                .filter(field => field.isVisible)
                .map(field => {
                    const fieldName = field.fieldName || '';
                    const metadata = entityFields?.custInfoFields[fieldName] || {
                        label: field.customLabel,
                        type: 'text',
                        defaultRequired: field.isMandatory
                    };

                    return (
                        <Col xs={24} sm={12} md={8} key={fieldName}>
                            {renderField(fieldName, metadata)}
                        </Col>
                    );
                });
        }

        return null;
    };

    return (
        <div className="space-y-4">
            <Typography.Title level={5} className={dark ? 'text-white' : ''}>
                Additional Customer Information
            </Typography.Title>

            <Form
                form={form}
                layout="vertical"
                onValuesChange={handleValuesChange}
                initialValues={formData.custInfoData}
            >
                <Row gutter={[16, 0]}>
                    {renderFields()}
                </Row>
            </Form>
        </div>
    );
};

export default AdditionalInfoStep;